'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Mail, Lock, Eye, EyeOff, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

const DEMO_CREDENTIALS = [
  { id: 'cred-importer', role: 'Importer', email: 'rahul.sharma@indoexim.co.in', password: 'Import@2026' },
  { id: 'cred-ff', role: 'Freight Forwarder', email: 'priya.nair@globalfreight.in', password: 'FF@2026secure' },
];

export default function LoginForm({ onSwitchToRegister }: { onSwitchToRegister: () => void }) {
  const [showPass, setShowPass] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginFormData>();

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const autofill = (email: string, password: string) => {
    setValue('email', email);
    setValue('password', password);
    toast.info('Demo credentials filled in — click Sign In to continue.');
  };

  const onSubmit = async (data: LoginFormData) => {
    // Backend integration point: POST /api/auth/login with email + password
    await new Promise((r) => setTimeout(r, 1200));
    const valid = DEMO_CREDENTIALS.some(
      (c) => c.email === data.email && c.password === data.password
    );
    if (!valid) {
      setError('root', { message: 'Invalid credentials — use the demo accounts below to sign in.' });
      return;
    }
    toast.success('Signed in successfully!');
    window.location.href = '/my-alerts';
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Welcome back</h1>
        <p className="text-sm text-muted-foreground mt-1">Sign in to manage your BL alerts</p>
      </div>

      {/* Root error */}
      {errors.root && (
        <div className="bg-danger/10 border border-danger/30 rounded-lg px-4 py-3 text-sm text-danger font-medium">
          {errors.root.message}
        </div>
      )}

      {/* Email */}
      <div>
        <label className="block text-sm font-semibold text-foreground mb-1.5">
          Email Address
        </label>
        <div className="relative">
          <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="email"
            placeholder="yourname@company.com"
            {...register('email', {
              required: 'Email is required.',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email.' },
            })}
            className="input-field w-full h-11 pl-9 pr-4 text-sm"
          />
        </div>
        {errors.email && <p className="text-xs text-danger mt-1">{errors.email.message}</p>}
      </div>

      {/* Password */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-semibold text-foreground">Password</label>
          <button type="button" className="text-xs text-accent hover:text-primary transition-colors font-medium">
            Forgot password?
          </button>
        </div>
        <div className="relative">
          <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type={showPass ? 'text' : 'password'}
            placeholder="Enter your password"
            {...register('password', { required: 'Password is required.' })}
            className="input-field w-full h-11 pl-9 pr-10 text-sm"
          />
          <button
            type="button"
            onClick={() => setShowPass(!showPass)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
        {errors.password && <p className="text-xs text-danger mt-1">{errors.password.message}</p>}
      </div>

      {/* Remember me */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" {...register('remember')} className="w-4 h-4 accent-primary rounded" />
        <span className="text-sm text-muted-foreground">Remember me for 30 days</span>
      </label>

      {/* Submit */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="btn-primary h-11 flex items-center justify-center gap-2 text-sm"
      >
        {isSubmitting ? (
          <><Loader2 size={16} className="animate-spin" /> Signing in...</>
        ) : (
          'Sign In to EzyTrack'
        )}
      </button>

      {/* Demo credentials */}
      <div className="bg-muted rounded-xl p-4 border border-border">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Demo Accounts
        </p>
        <div className="flex flex-col gap-2">
          {DEMO_CREDENTIALS.map((cred) => (
            <div key={cred.id} className="flex items-center justify-between gap-2 bg-card rounded-lg px-3 py-2.5 border border-border">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-primary">{cred.role}</p>
                <p className="text-xs text-muted-foreground truncate">{cred.email}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => handleCopy(cred.email, `${cred.id}-email`)}
                  className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  title="Copy email"
                >
                  {copiedField === `${cred.id}-email` ? <Check size={12} className="text-success" /> : <Copy size={12} />}
                </button>
                <button
                  type="button"
                  onClick={() => autofill(cred.email, cred.password)}
                  className="text-xs font-semibold text-accent hover:text-primary px-2 py-1 rounded bg-accent/10 hover:bg-accent/20 transition-colors"
                >
                  Use
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="text-sm text-center text-muted-foreground">
        Don't have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToRegister}
          className="text-accent font-semibold hover:text-primary transition-colors"
        >
          Register free
        </button>
      </p>
    </form>
  );
}